/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./index.css";
import { SEO_DEFAULTS, buildStructuredData } from "./seo";

function upsertMetaByName(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(url: string) {
  let link = document.querySelector(`link[rel="canonical"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function applySeoDefaults() {
  const pageUrl = window.location.href;
  const logoUrl = new URL("./logo.svg", import.meta.url).toString();

  document.title = SEO_DEFAULTS.title;
  upsertCanonical(pageUrl);

  upsertMetaByName("description", SEO_DEFAULTS.description);
  upsertMetaByName("keywords", SEO_DEFAULTS.keywords.join(","));
  upsertMetaByName("twitter:card", SEO_DEFAULTS.twitterCard);
  upsertMetaByName("twitter:title", SEO_DEFAULTS.title);
  upsertMetaByName("twitter:description", SEO_DEFAULTS.description);
  upsertMetaByName("twitter:image", logoUrl);

  upsertMetaByProperty("og:type", "website");
  upsertMetaByProperty("og:title", SEO_DEFAULTS.title);
  upsertMetaByProperty("og:description", SEO_DEFAULTS.description);
  upsertMetaByProperty("og:site_name", SEO_DEFAULTS.siteName);
  upsertMetaByProperty("og:url", pageUrl);
  upsertMetaByProperty("og:image", logoUrl);
  upsertMetaByProperty("og:locale", SEO_DEFAULTS.locale);

  const structuredData = buildStructuredData(window.location.origin);
  let structuredDataTag = document.getElementById("seo-structured-data");
  if (!structuredDataTag) {
    structuredDataTag = document.createElement("script");
    structuredDataTag.setAttribute("type", "application/ld+json");
    structuredDataTag.id = "seo-structured-data";
    document.head.appendChild(structuredDataTag);
  }
  structuredDataTag.textContent = JSON.stringify(structuredData);
}

function start() {
  applySeoDefaults();
  const root = createRoot(document.getElementById("root")!);
  root.render(<App />);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
